import moment from "moment-timezone";
import { hourLabel } from "./busy-times-utils";

export const RANGES = ["today", "yesterday", "7d", "30d", "custom"];
export const RESULTS = ["all", "opened", "denied", "emergency"];
export const PAGE_SIZE = 50;

const DAY = "YYYY-MM-DD";

export const defaultFilters = () => ({
  range: "7d",
  customFrom: "",
  customTo: "",
  member: null,
  doors: [],
  result: "all",
  unmatchedOnly: false,
  page: 1,
});

export const todayIn = (timeZone, now = new Date()) =>
  moment.tz(now, timeZone).format(DAY);

/**
 * Gym-local day range for a preset. The API takes the same `from`/`to` days,
 * so what the filter says is what the server counts.
 */
export const rangeDates = (filters, timeZone, now = new Date()) => {
  const today = todayIn(timeZone, now);
  const daysAgo = (days) =>
    moment.tz(today, DAY, timeZone).subtract(days, "days").format(DAY);
  switch (filters.range) {
    case "today":
      return { from: today, to: today };
    case "yesterday":
      return { from: daysAgo(1), to: daysAgo(1) };
    case "30d":
      return { from: daysAgo(29), to: today };
    case "custom":
      return {
        from: filters.customFrom || daysAgo(6),
        to: filters.customTo || today,
      };
    default:
      return { from: daysAgo(6), to: today };
  }
};

/** Filters -> query string params of door-logs / door-logs-summary / export. */
export const buildQuery = (filters, timeZone, now = new Date()) => {
  const { from, to } = rangeDates(filters, timeZone, now);
  return {
    from,
    to,
    ...(filters.member && { memberId: filters.member.id }),
    ...(filters.doors.length && {
      doors: filters.doors.map((door) => door.id).join(","),
    }),
    ...(filters.result !== "all" && { result: filters.result }),
    ...(filters.unmatchedOnly && { unmatchedOnly: true }),
  };
};

/** Anything other than "Last 7 days, everything" counts as filtered. */
export const isFiltered = (filters) =>
  filters.range !== "7d" ||
  Boolean(filters.member) ||
  filters.doors.length > 0 ||
  filters.result !== "all" ||
  filters.unmatchedOnly;

/** One entry per removable chip, in the order the prototype shows them. */
export const activeChips = (filters) => [
  ...(filters.range !== "7d" ? [{ key: "range" }] : []),
  ...(filters.member ? [{ key: "member", label: filters.member.name }] : []),
  ...filters.doors.map((door) => ({
    key: "door",
    id: door.id,
    label: door.label,
  })),
  ...(filters.result !== "all"
    ? [{ key: "result", value: filters.result }]
    : []),
  ...(filters.unmatchedOnly ? [{ key: "unmatched" }] : []),
];

export const removeChip = (filters, chip) => {
  const next = { ...filters, page: 1 };
  if (chip.key === "range") {
    return { ...next, range: "7d", customFrom: "", customTo: "" };
  }
  if (chip.key === "member") return { ...next, member: null };
  if (chip.key === "door") {
    return { ...next, doors: filters.doors.filter((d) => d.id !== chip.id) };
  }
  if (chip.key === "result") return { ...next, result: "all" };
  return { ...next, unmatchedOnly: false };
};

/**
 * Table rows grouped by gym-local day. `kind` lets the view say Today /
 * Yesterday (translated) or fall back to the date.
 */
export const groupByDay = (events, timeZone, now = new Date()) => {
  const today = todayIn(timeZone, now);
  const yesterday = moment
    .tz(today, DAY, timeZone)
    .subtract(1, "day")
    .format(DAY);
  const groups = [];
  for (const event of events) {
    const date = moment.tz(event.occurredAt, timeZone).format(DAY);
    let group = groups.at(-1);
    if (group?.date !== date) {
      let kind = "day";
      if (date === today) kind = "today";
      else if (date === yesterday) kind = "yesterday";
      group = {
        date,
        kind,
        events: [],
      };
      groups.push(group);
    }
    group.events.push(event);
  }
  return groups;
};

export const formatDay = (date, timeZone) =>
  moment.tz(date, DAY, timeZone).format("ddd, MMM D");

export const formatDateTime = (value, timeZone) =>
  moment.tz(value, timeZone).format("ddd, MMM D · h:mm a");

export const formatTime = (value, timeZone) =>
  moment.tz(value, timeZone).format("h:mm a");

/** "Showing 1–50 of 1,321". */
export const pageSpan = (page, total, pageSize = PAGE_SIZE) =>
  total === 0
    ? { from: 0, to: 0 }
    : {
        from: (page - 1) * pageSize + 1,
        to: Math.min(page * pageSize, total),
      };

export const pageCount = (total, pageSize = PAGE_SIZE) =>
  Math.max(1, Math.ceil(total / pageSize));

/** Which page buttons to show: first, last and a window around the current one. */
export const pageWindow = (page, pages) => {
  const wanted = new Set([1, pages, page - 1, page, page + 1]);
  if (page === 1) for (const n of [2, 3]) wanted.add(n);
  if (page === pages) for (const n of [pages - 1, pages - 2]) wanted.add(n);
  const numbers = [...wanted]
    .filter((n) => n >= 1 && n <= pages)
    .sort((a, b) => a - b);
  return numbers.flatMap((n, index) =>
    index > 0 && n - numbers[index - 1] > 1 ? ["gap", n] : [n]
  );
};

export const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const ONE_DAY = /^\d{4}-\d{2}-\d{2}$/;

const text = (value) => {
  if (typeof value === "number") return String(value);
  return typeof value === "string" ? value : "";
};
const csv = (value) =>
  text(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const doorOption = (door) => ({
  id: door.id,
  label: [door.displayName, door.locationName].filter(Boolean).join(" · "),
});

/**
 * Logs filters from the page's query string, so a filter change is a
 * navigation and the controller loads the matching data. Anything unknown
 * falls back to the default; doors the gym no longer has are dropped.
 */
export const filtersFromQuery = (query = {}, doorOptions = []) => {
  const range = RANGES.includes(query.range) ? query.range : "7d";
  const custom = range === "custom";
  const memberId = text(query.memberId);
  const memberName = text(query.memberName);
  const result = RESULTS.includes(query.result) ? query.result : "all";
  return {
    ...defaultFilters(),
    range,
    customFrom: custom && ONE_DAY.test(text(query.from)) ? query.from : "",
    customTo: custom && ONE_DAY.test(text(query.to)) ? query.to : "",
    member:
      OBJECT_ID.test(memberId) && memberName
        ? { id: memberId, name: memberName }
        : null,
    doors: csv(query.doors)
      .map((id) => doorOptions.find((door) => door.id === id))
      .filter(Boolean),
    result,
    unmatchedOnly: text(query.unmatched) === "1",
    page: Math.max(1, Number.parseInt(text(query.page), 10) || 1),
  };
};

/**
 * Filters -> body of `/actions/refresh`, which merges it into the current
 * query string. An empty string removes a key, so defaults are sent empty.
 */
export const queryFromFilters = (filters) => ({
  range: filters.range === "7d" ? "" : filters.range,
  from: filters.range === "custom" ? filters.customFrom : "",
  to: filters.range === "custom" ? filters.customTo : "",
  memberId: filters.member?.id ?? "",
  memberName: filters.member?.name ?? "",
  doors: filters.doors.map((door) => door.id).join(","),
  result: filters.result === "all" ? "" : filters.result,
  unmatched: filters.unmatchedOnly ? "1" : "",
  page: filters.page > 1 ? String(filters.page) : "",
});

/** Cards ignore result / unmatched-only and the page. */
export const summaryQuery = (filters, timeZone, now = new Date()) => {
  const { from, to, memberId, doors } = buildQuery(filters, timeZone, now);
  return {
    from,
    to,
    ...(memberId && { memberId }),
    ...(doors && { doors }),
  };
};

const series = (points, labels) =>
  points.some((point) => point > 0)
    ? { data: points, labels }
    : { data: undefined, labels: [] };

/**
 * Sparklines of the stat cards from `door-logs-summary`: today by hour (plain
 * and running total) and the selected range by day.
 */
export const summarySeries = (summary, timeZone) => {
  const hourly = summary?.rightNow?.hourly ?? [];
  const daily = summary?.range?.daily ?? [];
  const hourLabels = hourly.map((slot) => hourLabel(slot.hour));
  const dayLabels = daily.map((day) =>
    moment.tz(day.date, DAY, timeZone).format("ddd, MMM D")
  );
  let running = 0;
  return {
    hours: series(
      hourly.map((slot) => slot.visits),
      hourLabels
    ),
    cumulative: series(
      hourly.map((slot) => {
        running += slot.visits;
        return running;
      }),
      hourLabels
    ),
    visits: series(
      daily.map((day) => day.visits),
      dayLabels
    ),
    unique: series(
      daily.map((day) => day.uniqueMembers),
      dayLabels
    ),
    denied: series(
      daily.map((day) => day.denied),
      dayLabels
    ),
  };
};
